import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

// import { GetRegisters } from "api/rpc/facilityAdmin/facility/register/register";
import { StatusCode } from "api/protocols";

import Checkbox from "components/form/checkbox/Checkbox";
import Icon from "components/icon/Icon";
import { faCashRegister } from "@fortawesome/pro-regular-svg-icons";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";

import { IRegister, IRegisterGroup } from "pages/secure/facility/Admin";
import Portal from "elements/Portal";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { ButtonNew as Button } from "components/buttonNew";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import { OpenRegister } from "api/rpc/2024-04/facilityAdmin/facility/register/shift";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { loadRegisters as loadReduxRegisters } from "redux/actions/facility";
import { useHistory } from "react-router";
import { Badge } from "components/badge/Badge";

type RegistersModalProps = {
  open: boolean;
  onClose: (register?: IRegister) => void;
};

type RegisterModalState = {
  id: number;
  default_folder_id: number;
  openRegister?: boolean;
  starting_float?: string;
  register?: IRegister;
};

/* Return ID of register if found in localStorage */
function obtainStoredRegister(key: keyof RegisterModalState) {
  const registerJSON = localStorage.getItem("register");
  return registerJSON === null ? null : (JSON.parse(registerJSON)[key] as number);
}

/**
 * Display Facility Registers in a modal for selection. Organized by their RegisterGroups
 * - Default register if found within localStorage.
 * - GetRegisters extended={false} :: populates registers & groups being displayed
 */
export default function RegistersModal(props: RegistersModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [registers, setRegisters] = useState<{ registers: IRegister[]; groups: IRegisterGroup[] }>({
    registers: undefined,
    groups: undefined,
  });

  const [state, setState] = useState<RegisterModalState>({
    id: obtainStoredRegister("id"),
    default_folder_id: obtainStoredRegister("default_folder_id"),
    openRegister: false,
    starting_float: "",
    register: null,
  });

  const [refresh, setRefresh] = useState(false);

  // Currently selected localStorage Register
  const selectedRegister = registers.registers?.filter(value => {
    if (value.id === obtainStoredRegister("id")) {
      return true;
    }
  });

  // All other Registers
  const nonSelectedRegisters = registers.registers?.filter(value => {
    if (value.id !== obtainStoredRegister("id") && value.register_group_id) {
      return true;
    }
  });

  // List of register_group ID's
  const uniqueRegisterGroupIds = [...new Set(registers?.groups?.map(value => value?.id))];

  // Reload registers on modal open
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (props.open) {
      void loadRegisters(source.token);
      return;
    }

    return () => {
      source.cancel();
    };
  }, [props.open]);

  // loadRegisters only on Sheet onOk click
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (refresh) {
      void loadRegisters(source.token);
    }

    return () => {
      source.cancel();
    };
  }, [refresh]);

  async function loadRegisters(token: CancelToken) {
    if (registers.registers !== undefined) {
      setRegisters(prev => ({ ...prev, registers: undefined }));
    }

    const res = await GetRegisters({ extended: true, active_shift: true }, token ? false : true, token);

    if (res.status === StatusCode.OK && res.data) {
      setRegisters({
        registers: res.data,
        groups: res.data.map((value: IRegister) => value.register_group),
      });

      if (refresh) {
        setRefresh(false);
      }

      return;
    }

    setRegisters(prev => ({
      ...prev,
      registers: [],
    }));

    if (refresh) {
      setRefresh(false);
    }
  }

  // Reset State of Modal
  function handleOnClose() {
    setState({ id: obtainStoredRegister("id"), default_folder_id: obtainStoredRegister("default_folder_id") });
    props.onClose();
  }

  function handleRegisterClick(register: IRegister) {
    setState({ id: register.id, default_folder_id: register.default_folder_id, register: null });
    props.onClose(register);
  }

  function handleOpenRegisterModal(register: IRegister) {
    setState(prevState => ({ ...prevState, openRegister: true, register: register }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setState(prevState => ({ ...prevState, starting_float: value }));
  }

  async function handleOpenRegister() {
    const params = {
      register_id: state.register?.id,
      starting_float: Number(state.starting_float),
    };

    const openRes = await OpenRegister(params, true);

    if (openRes.status !== StatusCode.OK) {
      dispatch(showError(String(openRes.data)));

      return;
    }

    void dispatch(loadReduxRegisters(null, true));

    void handleRegisterClick({ ...state.register, active_shift: true });
  }

  function handleCloseRegister(register: IRegister) {
    history.push("/admin/settings/cash-out/new", { registerId: register.id });
    void handleOnClose();
  }

  return (
    <Portal isMounted={props.open}>
      <Sheet
        size="small"
        closable
        title={t("secure.facility.admin.021")}
        open={props.open}
        cancelText={t("secure.facility.admin.022")}
        onCancel={() => handleOnClose()}
        okText={t("Refresh")}
        onOk={() => setRefresh(true)}
        okDisabled={!registers.registers} // disable 'OK' if loading
      >
        <div className="ui-checkbox-group register-modal">
          {/* Registers Loading */}
          {!registers.registers ? (
            <div style={{ width: "100%", height: "55px" }}>
              <Spin />
            </div>
          ) : (
            <>
              {/* Currently Active Register */}
              {selectedRegister &&
                selectedRegister.map(register => {
                  return (
                    <Fragment key={register.id}>
                      <div>{t("Selected Register")}</div>
                      <div className="selected-register-container">
                        <div
                          key={register.id}
                          onClick={() => handleRegisterClick(register)}
                          className="selected-register"
                        >
                          <div className="flex items-center gap-3">
                            <Icon size="small" icon={faCashRegister} />

                            <p className="text-sm text-medium text-gray-700">{register.title}</p>

                            {register.active_shift && <Badge type="success">Open</Badge>}
                          </div>

                          <Checkbox
                            size="medium"
                            value={register.id}
                            checked={state.id === register?.id}
                            onChange={() =>
                              setState({ id: register.id, default_folder_id: register.default_folder_id })
                            }
                          />
                        </div>

                        <div>
                          <Button
                            onClick={() =>
                              register?.active_shift ? handleCloseRegister(register) : handleOpenRegisterModal(register)
                            }
                            type="text"
                          >
                            {" "}
                            {register?.active_shift ? "Close" : "Open"}
                          </Button>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}

              {/* All Other Registers, grouped by register_group */}
              {uniqueRegisterGroupIds &&
                uniqueRegisterGroupIds.map(register_group_id => {
                  return (
                    <Fragment key={register_group_id}>
                      {/* Do not show title if 0 nonSelectedRegisters */}
                      {nonSelectedRegisters
                        .filter(register => !register.disabled)
                        .some(register => register.register_group_id === register_group_id) && (
                        <div>{registers.groups?.find(value => value.id === register_group_id).title}</div>
                      )}
                      {/* Display Registers within the associated group */}
                      {nonSelectedRegisters
                        .filter(register => !register?.disabled)
                        ?.map(register => {
                          if (register.register_group_id === register_group_id) {
                            return (
                              <div className="unselected-register-container" key={register.id}>
                                <div className="unselected-register" onClick={() => handleRegisterClick(register)}>
                                  <div className="flex items-center gap-3">
                                    <Icon size="small" icon={faCashRegister} />

                                    <p className="text-sm text-medium text-gray-700">{register.title}</p>

                                    {register.active_shift && <Badge type="success">Open</Badge>}
                                  </div>
                                  <Checkbox size="medium" value={register.id} />
                                </div>

                                <div>
                                  <Button
                                    onClick={() =>
                                      register?.active_shift
                                        ? handleCloseRegister(register)
                                        : handleOpenRegisterModal(register)
                                    }
                                    type="text"
                                  >
                                    {" "}
                                    {register?.active_shift ? t("Close") : t("Open")}
                                  </Button>
                                </div>
                              </div>
                            );
                          }
                        })}
                    </Fragment>
                  );
                })}
            </>
          )}
        </div>
      </Sheet>

      <Popup
        open={state.openRegister}
        type="info"
        title={t("Open Register")}
        description={t("Please enter the starting float amount")}
        onOk={handleOpenRegister}
        okText={t("Open")}
        cancelText={t("Cancel")}
        stacked
        onCancel={() =>
          setState(prevState => ({
            ...prevState,
            openRegister: false,
            id: obtainStoredRegister("id"),
            default_folder_id: obtainStoredRegister("default_folder_id"),
            starting_float: "",
          }))
        }
      >
        <div className="pd-4 mt-4">
          <Input
            onChange={handleInputChange}
            value={state.starting_float}
            id="searchQuery"
            labelHidden
            type={"number"}
            placeholder="0.00"
          />
        </div>
      </Popup>
    </Portal>
  );
}
